import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFarm } from '../../contexts/FarmContext';
import { aiAPI, recordsAPI, farmAPI } from '../../services/api';

const AIAssistant = () => {
  const { currentFarm } = useFarm();
  const [searchParams] = useSearchParams();
  const [birds, setBirds] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const [cohorts, setCohorts] = useState([]);
  const [cohortId, setCohortId] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [didAutoAsk, setDidAutoAsk] = useState(false);
  
  // Conversation history
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed on mobile
  
  // Image upload
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [diagnosisMode, setDiagnosisMode] = useState(null); // null | 'health' | 'coop' | 'general'
  const [modeSelected, setModeSelected] = useState(false); // Hide selector after selection
  const fileInputRef = useRef(null);
  
  // Guided assessment modes
  const [guidedMode, setGuidedMode] = useState(null); // null | 'health_check' | 'coop_assessment'
  // Mode of the conversation being viewed. The server owns this; it is mirrored
  // here only so the session is visible to the farmer.
  const [sessionMode, setSessionMode] = useState(null);
  const [sessionComplete, setSessionComplete] = useState(false);
  
  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Scroll to bottom when messages change
  useEffect(() => {
    try {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    } catch (e) {
      console.error('Scroll error:', e);
    }
  }, [messages]);

  // Validate messages state - ensure it's always a valid array
  useEffect(() => {
    if (!Array.isArray(messages)) {
      console.error('Messages is not an array, resetting');
      setMessages([]);
    }
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations().catch(console.error);
  }, []);

  const loadConversations = async () => {
    try {
      setLoadingConversations(true);
      const res = await aiAPI.listConversations();
      setConversations(res.data?.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Load dynamic net birds, cohorts, analytics, and build neutral greeting
  useEffect(() => {
    const load = async () => {
      if (!currentFarm) return;
      try {
        const res = await recordsAPI.getBirds(currentFarm.id);
        setBirds(res.data);
        let cohortList = [];
        try {
          const cs = await farmAPI.listCohorts(currentFarm.id);
          cohortList = cs.data?.cohorts || [];
          setCohorts(cohortList);
        } catch {}
        try {
          const a = await recordsAPI.getAnalytics(currentFarm.id);
          setAnalytics(a.data || null);
        } catch {}
        // Build a neutral contextual greeting only if no active conversation
        if (!activeConversationId) {
          const total = res.data?.netBirds;
          const breeds = (Array.isArray(cohortList) ? cohortList : []).map(c => c.breed).filter(Boolean);
          const uniqueBreeds = Array.from(new Set(breeds)).slice(0, 3);
          const breedText = uniqueBreeds.length ? ` Breeds include ${uniqueBreeds.join(', ')}.` : '';
          setMessages([{ role: 'assistant', content: `Hi! I'm Omabra, your poultry assistant. I'm looking at your farm${typeof total === 'number' ? ` with ~${total} birds` : ''}.${breedText} Select a flock to focus advice, or ask me anything. You can also upload images for diagnosis!` }]);
        }
      } catch (_) {
        // keep default greeting
      }
    };
    load();
  }, [currentFarm, activeConversationId]);

  // When user selects a cohort, append an assistant note summarizing that flock and refresh analytics
  useEffect(() => {
    if (!cohortId || !cohorts?.length) return;
    const c = cohorts.find(x => String(x.id) === String(cohortId));
    if (!c) return;
    const start = c.startDate ? new Date(c.startDate) : null;
    const now = new Date();
    const ageDays = start ? Math.max(0, Math.floor((now - start) / (1000*60*60*24))) : 0;
    setMessages((m) => [
      ...(Array.isArray(m) ? m : []),
      { role: 'assistant', content: `Flock selected: ${c.name} (${c.type}${c.breed ? `, ${c.breed}` : ''}), start qty ${c.startQty}, approx. age ${ageDays} days. I will tailor answers to this flock until you change it.`}
    ]);
    // Refresh analytics for this cohort
    (async () => {
      try {
        if (!currentFarm) return;
        const a = await recordsAPI.getAnalytics(currentFarm.id, { cohortId });
        setAnalytics(a.data || null);
      } catch {}
    })();
  }, [cohortId, cohorts, currentFarm]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be less than 5MB');
        return;
      }
      setSelectedImage(file);
      setDiagnosisMode(null); // Reset mode when new image selected
      setModeSelected(false); // Show mode selector for new image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setDiagnosisMode(null);
    setModeSelected(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Convert image to base64
  const imageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  const send = async (overrideMessage = null, overrideGuidedMode = null) => {
    const messageToSend = overrideMessage || input.trim();
    
    if (!messageToSend && !selectedImage) return;
    
    // Require diagnosis mode selection when image is present and not already selected
    if (selectedImage && !modeSelected && !diagnosisMode) {
      alert('Please select how you want this image analyzed');
      return;
    }
    
    const userMsg = messageToSend || '';
    const hasImage = !!selectedImage;
    const isHealthDiagnosis = diagnosisMode === 'health';
    const isCoopAssessment = diagnosisMode === 'coop';
    const activeGuidedMode = overrideGuidedMode || guidedMode || null;
    
    setInput('');
    
    // Build appropriate message content
    let defaultMessage = '';
    if (hasImage) {
      if (isHealthDiagnosis) defaultMessage = '[🩺 Image uploaded for health diagnosis]';
      else if (isCoopAssessment) defaultMessage = '[🏠 Image uploaded for coop assessment]';
      else defaultMessage = '[📷 Image uploaded]';
    }
    
    // Add user message with image preview if present
    const displayContent = userMsg || defaultMessage || 'Message sent';
    const userMessageObj = { 
      role: 'user', 
      content: displayContent,
      ...(imagePreview ? { imageUrl: imagePreview } : {})
    };
    setMessages((m) => [...(Array.isArray(m) ? m : []), userMessageObj]);
    
    try {
      setLoading(true);
      
      // Optional cohort context
      let cohortCtx;
      if (cohortId && cohorts?.length) {
        const c = cohorts.find(x => String(x.id) === String(cohortId));
        if (c) {
          cohortCtx = {
            id: c.id,
            name: c.name,
            type: c.type,
            breed: c.breed,
            startQty: c.startQty,
            housingType: c.housingType,
            feedingSystem: c.feedingSystem,
            status: c.status
          };
        }
      }

      // Compose analytics summary for AI context
      const analyticsSummary = (() => {
        if (!analytics?.summary) return undefined;
        const s = analytics.summary;
        return {
          totalEggsCollected: Number.isFinite(s.totalEggs) ? s.totalEggs : undefined,
          totalFeedUsedKg: Number.isFinite(s.totalFeedKg) ? s.totalFeedKg : undefined,
          totalSalesKES: Number(s.totalSales ?? 0),
          totalExpensesKES: Number(s.totalExpenses ?? 0),
          profitKES: Number(s.profit ?? 0),
          profitMarginPct: Number(s.profitMargin ?? 0),
        };
      })();

      // Build farm-level neutral context (no type/age by default)
      const breedList = Array.from(new Set((cohorts || []).map(c => c.breed).filter(Boolean))).slice(0, 5);
      const context = currentFarm ? {
        farm: {
          id: currentFarm.id,
          name: currentFarm.name,
          location: currentFarm.location,
          totalBirds: birds?.netBirds,
          breeds: breedList
        },
        ...(analyticsSummary ? { analytics: analyticsSummary } : {}),
        ...(cohortCtx ? { cohort: cohortCtx } : {})
      } : undefined;

      // Prepare image if present
      let imageBase64 = null;
      if (selectedImage) {
        imageBase64 = await imageToBase64(selectedImage);
      }

      // Build default message based on mode
      let defaultApiMessage = 'Please analyze this image and provide your assessment.';
      if (hasImage) {
        if (isHealthDiagnosis) defaultApiMessage = 'Please perform a health diagnosis on this image.';
        else if (isCoopAssessment) defaultApiMessage = 'Please assess this coop/housing from the image.';
      }

      const res = await aiAPI.chat({
        message: userMsg || defaultApiMessage,
        conversationId: activeConversationId || undefined,
        farmId: currentFarm?.id,
        cohortId: cohortId || undefined,
        imageBase64,
        diagnosisMode: hasImage ? diagnosisMode : undefined,
        guidedMode: activeGuidedMode || undefined,
        context
      });
      
      const text = res.data?.response || 'No response';
      const newConversationId = res.data?.conversationId;
      setSessionMode(res.data?.guidedMode || null);
      setSessionComplete(!!res.data?.assessmentComplete);
      
      // Add assistant response first
      setMessages((m) => [...(Array.isArray(m) ? m : []), { role: 'assistant', content: String(text || 'No response received') }]);
      
      // Update active conversation if new (after message is added)
      if (newConversationId && newConversationId !== activeConversationId) {
        setActiveConversationId(newConversationId);
        // Delay conversation list refresh to avoid state conflicts
        setTimeout(() => {
          loadConversations().catch(console.error);
        }, 100);
      }
      
      // Clear image after sending
      removeImage();
      
      // Clear guided mode after first message is sent (conversation continues normally)
      if (activeGuidedMode) {
        setGuidedMode(null);
      }
      
    } catch (e) {
      console.error('Send error:', e);
      const errorMsg = e?.response?.data?.response || e?.response?.data?.message || e?.message || 'AI Assistant is unavailable. Please try again.';
      setMessages((m) => [...(Array.isArray(m) ? m : []), { role: 'assistant', content: String(errorMsg) }]);
    } finally {
      setLoading(false);
    }
  };

  // Start guided assessment (Health Check or Coop Assessment)
  // Each assessment starts a NEW conversation for focused, organized chats
  const startGuidedAssessment = async (mode) => {
    if (loading) return;
    
    // Build the display message based on mode
    // Must match GUIDED_STARTED_TEXT on the server, which is what gets saved to
    // the conversation and shown when it is reopened.
    const modeLabels = {
      health_check: '🩺 Started a health check',
      coop_assessment: '🏠 Started a coop assessment'
    };
    
    // Trigger messages that tell the AI to start guided mode
    const triggerMessages = {
      health_check: '[START_HEALTH_CHECK]',
      coop_assessment: '[START_COOP_ASSESSMENT]'
    };
    
    const displayMessage = modeLabels[mode] || 'Starting assessment...';
    const triggerMessage = triggerMessages[mode];
    
    // START FRESH: Clear current conversation and messages for a new assessment
    setActiveConversationId(null);
    setMessages([{ 
      role: 'user', 
      content: displayMessage,
      isSystemAction: true
    }]);
    
    // Make API call directly (don't use send() to avoid duplicate message)
    try {
      setLoading(true);
      
      // Build context
      let cohortCtx;
      if (cohortId && cohorts?.length) {
        const c = cohorts.find(x => String(x.id) === String(cohortId));
        if (c) {
          cohortCtx = {
            id: c.id,
            name: c.name,
            type: c.type,
            breed: c.breed,
            startQty: c.startQty,
            housingType: c.housingType,
            feedingSystem: c.feedingSystem,
            status: c.status
          };
        }
      }
      
      const breedList = Array.from(new Set((cohorts || []).map(c => c.breed).filter(Boolean))).slice(0, 5);
      const context = currentFarm ? {
        farm: {
          id: currentFarm.id,
          name: currentFarm.name,
          location: currentFarm.location,
          totalBirds: birds?.netBirds,
          breeds: breedList
        },
        ...(cohortCtx ? { cohort: cohortCtx } : {})
      } : undefined;
      
      // No conversationId = creates a NEW conversation
      const res = await aiAPI.chat({
        message: triggerMessage,
        farmId: currentFarm?.id,
        cohortId: cohortId || undefined,
        guidedMode: mode,
        context
      });
      
      const text = res.data?.response || 'No response';
      const newConversationId = res.data?.conversationId;
      setSessionMode(res.data?.guidedMode || mode);
      setSessionComplete(false);
      
      // Add assistant response
      setMessages((m) => [...(Array.isArray(m) ? m : []), { role: 'assistant', content: String(text || 'No response received') }]);
      
      // Set the new conversation ID
      if (newConversationId) {
        setActiveConversationId(newConversationId);
        // Refresh conversation list to show new assessment
        setTimeout(() => {
          loadConversations().catch(console.error);
        }, 100);
      }
      
    } catch (e) {
      console.error('Guided assessment error:', e);
      const errorMsg = e?.response?.data?.response || e?.response?.data?.message || e?.message || 'Failed to start assessment. Please try again.';
      setMessages((m) => [...(Array.isArray(m) ? m : []), { role: 'assistant', content: String(errorMsg) }]);
    } finally {
      setLoading(false);
    }
  };

  // Handle mode selection for image - hide selector after selection
  const handleModeSelect = (mode) => {
    setDiagnosisMode(mode);
    setModeSelected(true);
  };

  // Handle deep-link ask and cohort selection via query params
  useEffect(() => {
    const ask = searchParams.get('ask');
    const qCohortId = searchParams.get('cohortId');
    if (qCohortId && !cohortId) {
      setCohortId(qCohortId);
    }
    if (ask && !didAutoAsk && currentFarm) {
      setInput(ask);
      // send after small delay to allow cohorts to load
      const t = setTimeout(() => { setDidAutoAsk(true); send(); }, 300);
      return () => clearTimeout(t);
    }
  }, [searchParams, currentFarm, cohortId, didAutoAsk]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Conversation management
  const startNewConversation = () => {
    setActiveConversationId(null);
    setMessages([{ 
      role: 'assistant', 
      content: `Hi! I'm Omabra, your poultry assistant. How can I help today? You can ask questions or upload images for diagnosis.` 
    }]);
    removeImage();
    setGuidedMode(null);
    setSessionMode(null);
    setSessionComplete(false);
    setSidebarOpen(false); // Close sidebar on mobile after action
  };

  const loadConversation = async (convId) => {
    try {
      const res = await aiAPI.getConversation(convId);
      const conv = res.data?.conversation;
      if (conv) {
        setActiveConversationId(conv.id);
        // Defensive: ensure render never crashes if backend returns unexpected messages type.
        const msgs = Array.isArray(conv.messages) ? conv.messages : [];
        setMessages(msgs);
        setGuidedMode(null);
        setSessionMode(conv.guidedMode || null);
        setSessionComplete(
          msgs.some((m) => m.role === 'assistant' && /Assessment Summary|Urgency Level|Overall Rating/i.test(m.content || ''))
        );
        setSidebarOpen(false); // Close sidebar on mobile after selection
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await aiAPI.deleteConversation(convId);
      setConversations(c => (Array.isArray(c) ? c : []).filter(x => x.id !== convId));
      if (activeConversationId === convId) {
        startNewConversation();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  const shareConversation = async () => {
    if (!activeConversationId) return;
    try {
      const res = await aiAPI.shareConversation(activeConversationId);
      let url = res.data?.shareUrl;
      // If URL is relative (doesn't start with http), prepend origin
      if (!url || !url.startsWith('http')) {
        url = `${window.location.origin}/chat/shared/${res.data?.shareToken}`;
      }
      setShareUrl(url);
      setShowShareModal(true);
    } catch (err) {
      console.error('Failed to share conversation:', err);
      alert('Failed to generate share link');
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Format date for conversation list
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Group conversations by date
  const safeConversations = Array.isArray(conversations) ? conversations : [];
  const safeMessages = Array.isArray(messages) ? messages : [];
  const groupedConversations = safeConversations.reduce((acc, conv) => {
    const dateKey = formatDate(conv?.updatedAt);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(conv);
    return acc;
  }, {});

  return (
    <div className="flex h-[calc(100vh-7rem)] sm:h-[calc(100vh-7rem)] relative">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Conversation History */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-40
        w-72 sm:w-80 lg:w-64
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-0 lg:overflow-hidden'}
        bg-gradient-to-b from-gray-50 to-white border-r border-gray-200 flex flex-col
        shadow-xl lg:shadow-none
      `}>
        {/* Sidebar Header */}
        <div className="p-3 bg-gradient-to-r from-green-600 to-emerald-600 flex items-center justify-between">
          <button 
            onClick={startNewConversation}
            className="flex-1 bg-white text-green-700 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-xl hover:bg-green-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            New Chat
          </button>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden ml-2 p-2 bg-white/20 hover:bg-white/30 rounded-xl text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto p-2">
          {loadingConversations ? (
            <div className="text-center py-4 text-gray-500 text-sm">Loading...</div>
          ) : safeConversations.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-600">No conversations yet</p>
              <p className="text-xs text-gray-400 mt-1">Start a new chat to get help</p>
            </div>
          ) : (
            Object.entries(groupedConversations).map(([dateKey, convs]) => (
              <div key={dateKey} className="mb-4">
                <div className="text-xs text-gray-400 px-2 py-1 font-medium uppercase tracking-wider">{dateKey}</div>
                {convs.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => loadConversation(conv.id)}
                    className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer text-sm mb-1 transition-all ${
                      activeConversationId === conv.id 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    <span className="flex-1 truncate">{conv.title}</span>
                    <button 
                      onClick={(e) => deleteConversation(conv.id, e)}
                      className={`opacity-0 group-hover:opacity-100 p-1.5 rounded-lg transition-all ${activeConversationId === conv.id ? 'hover:bg-white/20' : 'hover:text-red-600 hover:bg-red-50'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-3 sm:p-4 text-white">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Menu Button */}
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-xl flex-shrink-0 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            {/* Title - Hidden on small screens */}
            <div className="hidden sm:block flex-shrink-0">
              <h1 className="text-lg font-bold">AI Assistant</h1>
              <p className="text-xs text-green-100">Diagnosis & advice</p>
            </div>

            {/* Mobile Title + Share */}
            <div className="sm:hidden flex-1 min-w-0 flex items-center gap-2">
              <div className="min-w-0">
                <h1 className="text-sm font-bold truncate">AI Assistant</h1>
                <p className="text-xs text-green-100 truncate">Diagnosis & advice</p>
              </div>
              {activeConversationId && (
                <button 
                  onClick={shareConversation}
                  className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg flex-shrink-0 transition-colors"
                  title="Share"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Flock Selector */}
            {currentFarm && (
              <select 
                className="w-24 sm:w-40 text-xs sm:text-sm py-2 px-3 bg-white/20 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/50 flex-shrink-0 text-white"
                value={cohortId} 
                onChange={(e)=>setCohortId(e.target.value)}
              >
                <option value="" className="text-gray-900">All</option>
                {cohorts.map(c => (
                  <option key={c.id} value={c.id} className="text-gray-900">{c.name}</option>
                ))}
              </select>
            )}
            
            {/* Share Button - Desktop only */}
            {activeConversationId && (
              <button 
                onClick={shareConversation}
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 rounded-xl flex-shrink-0 transition-colors text-sm"
                title="Share conversation"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div ref={listRef} className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gray-50">
          {safeMessages.filter(m => m && m.role).map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[90%] sm:max-w-[80%] ${m.role === 'user' ? 'order-1' : 'order-2'}`}>
                {/* Show image if present */}
                {m.imageUrl && (
                  <div className={`mb-2 ${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                    <img 
                      src={m.imageUrl} 
                      alt="Uploaded" 
                      className="inline-block max-h-40 sm:max-h-48 rounded-lg border border-gray-200"
                    />
                  </div>
                )}
                <div className={`px-3 sm:px-4 py-2 rounded-2xl ${
                  m.role === 'user' 
                    ? 'bg-primary-600 text-white rounded-br-md' 
                    : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                }`}>
                  <div className="text-sm whitespace-pre-wrap">{m.content || ''}</div>
                </div>
                <div className={`text-xs text-gray-400 mt-1 px-1 ${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                  {m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white text-gray-600 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Image Preview with Diagnosis Mode Selector */}
        {imagePreview && (
          <div className="px-3 sm:px-4 py-3 bg-gray-100 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Image thumbnail */}
              <div className="relative flex-shrink-0">
                <img src={imagePreview} alt="Preview" className="h-20 sm:h-24 rounded-lg border border-gray-300" />
                <button 
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-md"
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Mode Selector - Only show if mode not yet selected */}
              {!modeSelected ? (
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700 mb-2">What would you like to do with this image?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <button
                      onClick={() => handleModeSelect('health')}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-red-400 hover:bg-red-50 transition-all text-sm font-medium"
                    >
                      <span className="text-lg">🩺</span>
                      <span>Health Diagnosis</span>
                    </button>
                    <button
                      onClick={() => handleModeSelect('coop')}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-amber-400 hover:bg-amber-50 transition-all text-sm font-medium"
                    >
                      <span className="text-lg">🏠</span>
                      <span>Coop Assessment</span>
                    </button>
                    <button
                      onClick={() => handleModeSelect('general')}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50 transition-all text-sm font-medium"
                    >
                      <span className="text-lg">💬</span>
                      <span>General Question</span>
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    Select how you want this image analyzed
                  </p>
                </div>
              ) : (
                /* Compact view after mode selected */
                <div className="flex-1 flex items-center">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
                    diagnosisMode === 'health' ? 'bg-red-100 text-red-700' :
                    diagnosisMode === 'coop' ? 'bg-amber-100 text-amber-700' :
                    'bg-primary-100 text-primary-700'
                  }`}>
                    <span>{diagnosisMode === 'health' ? '🩺' : diagnosisMode === 'coop' ? '🏠' : '💬'}</span>
                    <span>
                      {diagnosisMode === 'health' ? 'Health Diagnosis' :
                       diagnosisMode === 'coop' ? 'Coop Assessment' : 'General Question'}
                    </span>
                  </div>
                  <button
                    onClick={() => setModeSelected(false)}
                    className="ml-2 text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Change
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active guided session indicator */}
        {sessionMode && (
          <div className={`px-3 sm:px-4 py-2 border-t flex items-center gap-2 text-xs ${
            sessionMode === 'health_check'
              ? 'border-red-100 bg-red-50 text-red-800'
              : 'border-amber-100 bg-amber-50 text-amber-800'
          }`}>
            <span>{sessionMode === 'health_check' ? '🩺' : '🏠'}</span>
            <span className="font-medium">
              {sessionMode === 'health_check' ? 'Health check' : 'Coop assessment'} in progress
            </span>
            <span className="opacity-75">
              {sessionComplete
                ? '- assessment delivered, ask anything to follow up'
                : '- answer the questions to build the assessment'}
            </span>
          </div>
        )}

        {/* Quick Actions */}
        {!imagePreview && (
          <div className="px-3 sm:px-4 py-2 border-t border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <span className="text-xs text-gray-500 flex-shrink-0">Quick:</span>
              <button
                onClick={() => startGuidedAssessment('health_check')}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-200 text-red-700 rounded-full hover:bg-red-50 hover:border-red-300 transition-colors text-xs font-medium flex-shrink-0 disabled:opacity-50"
              >
                <span>🩺</span>
                <span>Health Check</span>
              </button>
              <button
                onClick={() => startGuidedAssessment('coop_assessment')}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-200 text-amber-700 rounded-full hover:bg-amber-50 hover:border-amber-300 transition-colors text-xs font-medium flex-shrink-0 disabled:opacity-50"
              >
                <span>🏠</span>
                <span>Coop Assessment</span>
              </button>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-2 sm:p-3 border-t border-gray-200 bg-white safe-area-bottom">
          <div className="flex items-end gap-1.5 sm:gap-2">
            {/* Image Upload Button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleImageSelect}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2.5 sm:p-3 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
              title="Take photo or upload image"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            
            {/* Text Input */}
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e)=>setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Ask anything..."
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm sm:text-base"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
            </div>
            
            {/* Send Button */}
            <button 
              className="p-2.5 sm:p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 transition-colors" 
              // Important: don't pass the click event into `send()` (it becomes `overrideMessage`).
              onClick={() => send()} 
              disabled={loading || (!input.trim() && !selectedImage)}
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          
          {/* Helper Text - Only on larger screens */}
          <p className="hidden sm:block mt-2 text-xs text-gray-500 text-center">
            {selectedImage ? '📷 Image ready • ' : ''}
            Press Enter to send
            {!currentFarm && ' • Select a farm for better context'}
          </p>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowShareModal(false)}>
          <div 
            className="bg-white rounded-t-2xl sm:rounded-xl p-4 sm:p-6 w-full sm:max-w-md shadow-xl" 
            onClick={e => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />
            <h3 className="text-lg font-semibold mb-3">Share Conversation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Anyone with this link can view this conversation (read-only).
            </p>
            <div className="flex gap-2">
              <input 
                type="text" 
                value={shareUrl} 
                readOnly 
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
              />
              <button 
                onClick={copyShareLink}
                className={`px-4 py-2 rounded-lg font-medium text-white transition-colors ${copySuccess ? 'bg-green-600' : 'bg-primary-600 hover:bg-primary-700'}`}
              >
                {copySuccess ? '✓' : 'Copy'}
              </button>
            </div>
            <button 
              onClick={() => setShowShareModal(false)}
              className="mt-4 w-full py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
